import { IconOption } from "@kaggle/material";
import { Kaggle } from "@kaggle/sdk";
import * as moment from "moment";

export const getDatabundleWorkerEnvironment = () => {
  // TODO put environment in context
  switch (window.location.hostname) {
    case "localhost":
      return "development";
    case "staging.kaggle.com":
      return "staging";
    case "kaggle.com":
      return "production";
  }

  // Assume prod for everything else
  return "production";
};

export const getMaxBytes = (
  limits: Partial<Kaggle.Sdk.Datasets.Models.DatasetLimits>,
  isPrivate: boolean
): number | undefined => {
  if (!limits) {
    return;
  }

  if (isPrivate && limits.privateLimits) {
    return limits.privateLimits.remainingBytes;
  } else if (!isPrivate && limits.publicLimits) {
    return limits.publicLimits.maxBytes;
  }

  return;
};

export const getMaxFiles = (
  limits: Partial<Kaggle.Sdk.Datasets.Models.DatasetLimits>
) => {
  if (!limits) {
    return;
  }

  return limits.maxFiles;
};

export const isFileCompressed = (file: File): boolean => {
  const supportedCompressionTypes = [
    "application/zip",
    "application/x-zip",
    "application/x-zip-compressed",
    "application/x-compressed",
    "application/x-compress",
    "application/x-7z-compressed",
    "application/x-rar-compressed",
    "application/gzip",
    "application/gzipped",
    "application/gzip-compressed",
    "application/gunzip",
    "application/x-gzip",
    "gzip/document",
  ];

  return (
    supportedCompressionTypes.filter(type => type === file.type).length > 0
  );
};

export const isFileMaybeFolder = (file: File) => {
  if (file.type && file.type !== "") {
    return false; // Type detected -> File
  } else if (file.size > 1048576) {
    return false; // Large -> File
  } else if (file.size % 4096 !== 0) {
    return false; // Apparently, folders come in 4kb blocks
  }

  return true;
};

export const normalizeBounds = (
  value: number,
  lowerBound: number,
  upperBound: number
) => {
  if (value < lowerBound) {
    return lowerBound;
  } else if (value > upperBound) {
    return upperBound;
  } else {
    return value;
  }
};

export const getDatabundleProcessingMessage = (percent: number) => {
  if (Math.random() < 0.01) {
    return databundleProcessingMessageEasterEgg;
  } else if (percent > 1) {
    return databundleProcessingMessageFinal;
  } else if (percent <= 0) {
    return databundleProcessingMessages[0];
  } else {
    const index = Math.floor(percent * databundleProcessingMessages.length);
    return databundleProcessingMessages[index];
  }
};

export const estimateTimePercent = (creationStart: string, size: number) => {
  const startedDate = moment(creationStart);
  const now = moment();
  const estimatedSeconds = roughEstimateDatabundleProcessingTime(size);

  // Since we have a start date and an estimate, use this and average for % calculation (more accurate).
  const elapsedSeconds = now.diff(startedDate, "seconds");
  const timePercent = normalizeBounds(elapsedSeconds / estimatedSeconds, 0, 1);
  return timePercent;
};

export const estimateTimeHumanized = (size: number) => {
  const estimatedSeconds = roughEstimateDatabundleProcessingTime(size);
  const estimatedTime = moment.duration(estimatedSeconds, "seconds").humanize();
  return estimatedTime;
};

export const roughEstimateDatabundleProcessingTime = (
  totalCompressedSize: number
) => {
  const kilobyte = 1024;
  const megabyte = 1024 * kilobyte;
  const gigabyte = 1024 * megabyte;

  const minute = 60;
  const hour = 60 * minute;

  if (!totalCompressedSize) {
    return 10; // Minimum 10s
  }

  if (totalCompressedSize > 50 * gigabyte) {
    return 3 * hour;
  } else if (totalCompressedSize > 10 * gigabyte) {
    return 1 * hour;
  } else if (totalCompressedSize > 5 * gigabyte) {
    return 30 * minute;
  } else if (totalCompressedSize > 1 * gigabyte) {
    return 15 * minute;
  } else if (totalCompressedSize > 500 * megabyte) {
    return 10 * minute;
  } else if (totalCompressedSize > 100 * megabyte) {
    return 3 * minute;
  } else if (totalCompressedSize > 10 * megabyte) {
    return 1 * minute;
  } else if (totalCompressedSize > 1 * megabyte) {
    return 20;
  } else {
    return 10;
  }
};

// Renders date string from milliseconds since January 1, 1970, 00:00:00 UTC.
// reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date
export const renderDateFromMilliseconds = (value: number) => {
  return renderDate(new Date(value));
};

export const renderDateTimeFromMilliseconds = (value: number) => {
  return renderDateTime(new Date(value));
};

export const renderDate = (value: Date) => {
  if (!value) {
    return "";
  }
  return moment(value).format("DMMMYY");
};

export const renderDateTime = (value: Date) => {
  if (!value) {
    return "";
  }
  return moment(value).format("YYYY-MM-DD HH:mm:ss");
};

export const renderNumericalValue = (value: number, isInteger: boolean) => {
  if (value === null) {
    return "";
  }

  if (value === Infinity) {
    return "Inf";
  } else if (value === -Infinity) {
    return "-Inf";
  }

  if (isInteger) {
    return renderIntValue(value);
  }

  return renderDecimalValue(value);
};

export const renderIntValue = (value?: number) => {
  if (typeof value !== "number") {
    return "";
  }

  if (value > 100000000000) {
    return (value / 1000000000).toFixed(0) + "b";
  } else if (value > 10000000000) {
    return (value / 1000000000).toFixed(1) + "b";
  } else if (value > 1000000000) {
    return (value / 1000000000).toFixed(2) + "b";
  } else if (value > 100000000) {
    return (value / 1000000).toFixed(0) + "m";
  } else if (value > 10000000) {
    return (value / 1000000).toFixed(1) + "m";
  } else if (value > 1000000) {
    return (value / 1000000).toFixed(2) + "m";
  } else if (value > 100000) {
    return (value / 1000).toFixed(0) + "k";
  } else if (value > 10000) {
    return (value / 1000).toFixed(1) + "k";
  } else {
    return value.toFixed(0);
  }
};

export const renderDecimalValue = (value?: number): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (isNaN(value)) {
    return "NaN";
  }

  const absValue = Math.abs(value);

  if (absValue > 1e11) {
    return `${convertToDecimalString(value / 1e9, 0)}b`;
  } else if (absValue > 1e10) {
    return `${convertToDecimalString(value / 1e9, 1)}b`;
  } else if (absValue > 1e9) {
    return `${convertToDecimalString(value / 1e9, 2)}b`;
  } else if (absValue > 1e8) {
    return `${convertToDecimalString(value / 1e6, 0)}m`;
  } else if (absValue > 1e7) {
    return `${convertToDecimalString(value / 1e6, 1)}m`;
  } else if (absValue > 1e6) {
    return `${convertToDecimalString(value / 1e6, 2)}m`;
  } else if (absValue > 1e5) {
    return `${convertToDecimalString(value / 1e3, 0)}k`;
  } else if (absValue > 1e4) {
    return `${convertToDecimalString(value / 1e3, 1)}k`;
  } else if (absValue > 1e3) {
    return `${convertToDecimalString(value / 1e3, 2)}k`;
  } else if (absValue > 100) {
    return convertToDecimalString(value, 0);
  } else if (absValue > 10) {
    return convertToDecimalString(value, 1);
  } else {
    return convertToDecimalString(value, 2);
  }
};

export const convertToDecimalString = (
  value: number,
  maxDecimalCount: number
): string => {
  return Number.parseFloat(value.toFixed(maxDecimalCount)).toString();
};

/**
 * Formats a bytes to string.
 *
 * For example:
 *      formatBytes(1234);       // 1.21 KB
 *
 * @param bytes value in bytes to be formatted into a string.
 * @param decimals number of decimals present.
 */
export const formatBytes = (bytes: number, decimals?: number) => {
  const byteSize = findByteSize(bytes, decimals);
  return `${byteSize.value}${byteSize.unit}`;
};

export const findByteSize = (bytes: number, decimals?: number) => {
  if (bytes === 0) {
    return { value: 0, unit: "Bytes" };
  }

  const k = 1024;
  const dm = decimals || 2;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return {
    value: parseFloat((bytes / Math.pow(k, i)).toFixed(dm)),
    unit: sizes[i],
  };
};

export const databundleProcessingMessages = [
  "Revving our hard drives...",
  "Beaming data to servers...",
  "Reticulating files...",
  "Checking data types...",
  "Tabulating tabular files...",
  "Calculating metrics...",
  "Bundling blob files...",
  "Applying middle out compression...",
  "Activating Kernels...",
  "Transmogrifying data...",
  "Re-reticulating files...",
  "Applying final touches...",
];

export const databundleProcessingMessageEasterEgg = "Getting to the choppa...";
export const databundleProcessingMessageFinal = "Still working...";

export const iconFromExtension = (extension: string): IconOption => {
  let fileType =
    extension.substring(0, 1) === "." ? extension.substring(1) : extension;
  fileType = fileType ? fileType.toLowerCase() : fileType;
  switch (fileType) {
    case "csv":
    case "tsv":
    case "xls":
    case "xlsx":
      return "table_chart";
    case "sqlite":
      return "storage";
    case "sqlitetable":
      return "table_chart";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return "image";
    case "txt":
    case "json":
    case "ndjson":
      return "description";
    case "zip":
    case "7z":
    case "gz":
    case "tar":
    case "rar":
    case "bz2":
      return "drive_zip_outline";
    /*
    case "py":
      return "python";
    case "dockerfile":
      return "docker";
      */
    default:
      return "insert_drive_file";
  }
};

export const getExtension = (filename: string) => {
  return (
    filename.substring(filename.lastIndexOf(".") + 1, filename.length) ||
    filename
  );
};

export const maxNestedChildren = 10;
export const maxNestedIdeChildren = 50;

export const getGoogDatasetsHref = (search: string) => {
  const regex = /tag:('?)(\w+)('?)/;
  const sanitized = search.replace(regex, "$2").trim();
  return `https://toolbox.google.com/datasetsearch/search?query=${encodeURIComponent(
    sanitized
  )}%20-site%3Akaggle.com`;
};
